package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initWhatIsInAKotlinNativeBinaryPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/WhatIsInAKotlinNativeBinary.md", FrontMatterElement.Builder { addScalar("title", "What is in a Kotlin/Native binary, and where its time goes"); addScalar("searchTitle", "Kotlin/Native binary size and CPU profiling, measured"); addScalar("date", "2026-09-12"); addScalar("description", "One symbol table answers both questions. Every expectation I brought to it was wrong: the runtime is a 40 KB constant, a fifth of the file is symbol names, most of a release binary is not Kotlin, and a sampling profiler written in Kotlin hangs the process it profiles."); }))
}

@Page("/blog/what-is-in-a-kotlin-native-binary")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun WhatIsInAKotlinNativeBinaryPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bloaty")
        }
        org.jetbrains.compose.web.dom.Text(" will tell you that ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kfun:io.ktor.server.engine#embeddedServer(...)")
        }
        org.jetbrains.compose.web.dom.Text(" is 4,112 bytes. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("perf")
        }
        org.jetbrains.compose.web.dom.Text(" will tell you the same mangled symbol was on the stack. Both are true and neither is something you can act on, because neither knows that a Kotlin package is a thing.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So I wrote ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/razves") {
            org.jetbrains.compose.web.dom.Text("razves")
        }
        org.jetbrains.compose.web.dom.Text(", which reads the symbol table of a Kotlin/Native binary and aggregates it into packages and klib modules — first for bytes, then, out of the same table, for time. What follows is what the measurements said, including the six places where they contradicted the plan I started with.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything below is Kotlin 2.4.10, measured on six real binaries — an OIDC provider that links OpenSSL and a Rust database driver, a conformance client, razves' own two, and a fixture that is as close to an empty program as a Kotlin/Native executable gets.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-runtime-is-a-constant-and-it-is-small") }) {
        org.jetbrains.compose.web.dom.Text("The runtime is a constant, and it is small")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("37,889 to 40,871 bytes")
        }
        org.jetbrains.compose.web.dom.Text(", across all six binaries — a spread of under 3 KB over a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("43× range of file size")
        }
        org.jetbrains.compose.web.dom.Text(". Its share of the file falls from 17.6% in the smallest to 0.2% in the largest.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Which means the sentence \"the Kotlin/Native runtime is 17.6% of my binary\" tells you your binary does almost nothing. It never tells you the runtime is heavy.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The floor underneath it: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("496,232 bytes")
        }
        org.jetbrains.compose.web.dom.Text(" for one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("println")
        }
        org.jetbrains.compose.web.dom.Text(" and one dependency. Of that, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("135,032 bytes are the symbol table")
        }
        org.jetbrains.compose.web.dom.Text(" — the names, not the code.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("most-of-a-release-binary-is-not-your-code-and-not-kotlin-either") }) {
        org.jetbrains.compose.web.dom.Text("Most of a release binary is not your code, and not Kotlin either")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In the largest subject, Kotlin accounts for ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("36–40% of the attributed bytes")
        }
        org.jetbrains.compose.web.dom.Text(". The majority is statically linked C: OpenSSL, arriving through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ktor-client-curl")
        }
        org.jetbrains.compose.web.dom.Text(". Where a Rust-backed database driver is used, about ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("1.3 MB of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("tokio")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("sqlx")
            }
        }
        org.jetbrains.compose.web.dom.Text(" comes with it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("2,366,786 bytes of that binary are defined by two different builds of OpenSSL at once")
        }
        org.jetbrains.compose.web.dom.Text(" — which you can only see if you ask which archive defines each symbol, because the C ABI has no namespaces and no grammar over names can tell you.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-fifth-of-the-file-is-names") }) {
        org.jetbrains.compose.web.dom.Text("A fifth of the file is names")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("19–27% of every one of the six binaries is the symbol table.")
        }
        org.jetbrains.compose.web.dom.Text(" It is the largest single removable thing in a Kotlin/Native executable — and it is exactly what any of this reads. Strip it and the binary gets a fifth smaller and stops being explicable.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Debug builds are a different scale of the same point. The same program, debug against release: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("5,352,944 bytes down to 496,232, −90.7%")
        }
        org.jetbrains.compose.web.dom.Text(". Nine tenths of a debug binary is debug information: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".debug_str")
        }
        org.jetbrains.compose.web.dom.Text(" 1,860,741 bytes, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".debug_info")
        }
        org.jetbrains.compose.web.dom.Text(" 1,145,773, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".debug_line")
        }
        org.jetbrains.compose.web.dom.Text(" 1,011,093 — all of it gone in release, where what remains is the floor.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-trap-that-would-have-made-all-of-it-wrong") }) {
        org.jetbrains.compose.web.dom.Text("The trap that would have made all of it wrong")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Rust's legacy mangling starts with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("_ZN")
        }
        org.jetbrains.compose.web.dom.Text(". So does Itanium C++.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Group them together — which is the obvious first pass, and which I wrote — and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("1,263,149 bytes")
        }
        org.jetbrains.compose.web.dom.Text(" of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("tokio")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sqlx")
        }
        org.jetbrains.compose.web.dom.Text(" land in a row labelled \"the Kotlin/Native runtime\". That is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("31 times")
        }
        org.jetbrains.compose.web.dom.Text(" what the runtime actually is in that binary: 40,871 bytes. The report would have named the Kotlin/Native runtime the third-largest thing in the file, and the number would have been somebody else's code.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A Rust legacy symbol ends in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("17h")
        }
        org.jetbrains.compose.web.dom.Text(" followed by sixteen hex digits and an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("E")
        }
        org.jetbrains.compose.web.dom.Text(". That suffix is the whole difference between a table you can trust and a table that is confidently wrong.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-toolchain-does-not-ship-a-binary-reader") }) {
        org.jetbrains.compose.web.dom.Text("The toolchain does not ship a binary reader")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The plan was to wrap ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-nm")
        }
        org.jetbrains.compose.web.dom.Text(": it comes with Kotlin/Native, so the dependency is free.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It does not come with Kotlin/Native. The LLVM distribution the compiler downloads is the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("…-essentials-")
        }
        org.jetbrains.compose.web.dom.Text(" one, and it contains no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-nm")
        }
        org.jetbrains.compose.web.dom.Text(", no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-size")
        }
        org.jetbrains.compose.web.dom.Text(", no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-objdump")
        }
        org.jetbrains.compose.web.dom.Text(", no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("llvm-strip")
        }
        org.jetbrains.compose.web.dom.Text(". A free dependency that is not there is not free.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So razves reads ELF and Mach-O itself, with no subprocess at all. That turned out to matter a second time: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("nm")
            }
            org.jetbrains.compose.web.dom.Text(" reports a size of zero for every Mach-O symbol")
        }
        org.jetbrains.compose.web.dom.Text(", because the format records none. Apple targets need sizes derived from the distance to the next symbol — an algorithm, not a flag.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-same-table-asked-about-time") }) {
        org.jetbrains.compose.web.dom.Text("The same table, asked about time")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A sampling profiler needs three things razves already had: a symbol table reader, an address-to-symbol map, and a Kotlin mangling grammar. So the profiler is the same tool asked a different question — and the in-process half is deliberately tiny: a timer, a signal handler, a fixed ring of stacks. Raw addresses leave the process; every name is resolved afterwards, out of the binary.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The plan was to write that handler in Kotlin.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-kotlin-signal-handler-hangs-the-process") }) {
        org.jetbrains.compose.web.dom.Text("A Kotlin signal handler hangs the process")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("handler")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rate")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("runs")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("completed")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("crashed")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("hung")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Kotlin")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("100 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("3")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Kotlin")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 kHz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Kotlin, atomic increment only")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 kHz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("6")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Kotlin, non-allocating workload")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("100 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("C, via cinterop")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("100 Hz — 10 kHz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10 each")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three readings, and none of them is \"it works\". The failure rate ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("rises with the sampling rate")
        }
        org.jetbrains.compose.web.dom.Text(". It ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("disappears when the sampled workload stops allocating")
        }
        org.jetbrains.compose.web.dom.Text(" — the handler is interrupting the allocator. And it is not what the handler does: one that only increments an atomic fails just as often, because what is unsafe is entering the Kotlin runtime from a signal at all.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The same sampler in C completed every run at every rate. That is the whole reason the in-process half of this thing is forty lines of C behind a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".def")
        }
        org.jetbrains.compose.web.dom.Text(" file.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-rate-you-ask-for-is-not-the-rate-you-get") }) {
        org.jetbrains.compose.web.dom.Text("The rate you ask for is not the rate you get")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("timer")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("requested")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("delivered")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("setitimer(ITIMER_PROF)")
                    }
                    org.jetbrains.compose.web.dom.Text(", Linux")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1,000 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("219 Hz")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("timer_create(CLOCK_THREAD_CPUTIME_ID)")
                    }
                    org.jetbrains.compose.web.dom.Text(", Linux")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1,000 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("207 Hz")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("timer_create(CLOCK_MONOTONIC)")
                    }
                    org.jetbrains.compose.web.dom.Text(", Linux")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10,000 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8,204 Hz")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("setitimer(ITIMER_REAL)")
                    }
                    org.jetbrains.compose.web.dom.Text(", macOS")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1,000 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("964 Hz")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("setitimer(ITIMER_PROF)")
                    }
                    org.jetbrains.compose.web.dom.Text(", macOS")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1,000 Hz")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("334 Hz")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both CPU-time clocks on Linux saturate at the same ~200 Hz, and the API has nothing to do with it: a process or thread CPU clock is advanced on the scheduler tick, so asking a tick-bound clock for a millisecond gets you four. A monotonic timer is driven by a high-resolution one and delivers what it was asked for.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("They are not interchangeable — one samples a thread only while it runs, the other whether it runs or not — but whichever you choose, a profile that quotes the rate it ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("requested")
        }
        org.jetbrains.compose.web.dom.Text(" has percentages that mean nothing. Count the ticks.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-sampling-costs-a-stand-that-refused-to-answer") }) {
        org.jetbrains.compose.web.dom.Text("What sampling costs: a stand that refused to answer")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first number I wanted was \"sampling costs X% of CPU at 100 Hz\". I still do not have it, and the reason is worth more than the figure.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The machine's own run-to-run spread was 24–56% of its median. At 8.2 kHz — eighty times the rate in question — the sampled runs came out ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("faster")
        }
        org.jetbrains.compose.web.dom.Text(" than the unsampled ones in four pairs out of seven. Any percentage from that stand would have been noise wearing a suit.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the stand was rebuilt: both halves of each pair in one process, seconds apart, alternating which runs first, pinned to one core, and — this is the part that matters — it ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("measures its own resolving power")
        }
        org.jetbrains.compose.web.dom.Text(" before it reports anything, by doing a known amount of extra work and checking it can see it. On the build machine it resolves 5%. At about 900 Hz the cost of sampling is under that, so the honest form is a bound, not a figure.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then it refused the shortcut, too. Measure where the cost ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("is")
        }
        org.jetbrains.compose.web.dom.Text(" visible and scale down? The delivered rate rose from ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("7,216 Hz to 17,944 Hz")
        }
        org.jetbrains.compose.web.dom.Text(" and the cost went from ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("6.59% to 7.22%")
        }
        org.jetbrains.compose.web.dom.Text(" — two and a half times the work for no change worth the name. Whatever dominates it at those rates is not per-sample work, so no high-rate measurement can be divided down to 100 Hz.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-profiler-was-in-its-own-profile") }) {
        org.jetbrains.compose.web.dom.Text("The profiler was in its own profile")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first end-to-end run reported ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("100% of the time in one C function")
        }
        org.jetbrains.compose.web.dom.Text(" on a workload that is mostly Kotlin.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("backtrace()")
        }
        org.jetbrains.compose.web.dom.Text(" is called from inside the signal handler, so its frame 0 is the handler and frame 1 is the kernel trampoline. Every sample had the same leaf. Every unit test passed while it did — because they all handed the aggregation stacks a test had made up.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The fix is not a bigger skip count: the interrupted program counter is sitting in the context the kernel hands the handler, exact and free. Before and after, same program, same rate:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("leaf in C")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("leaf in Kotlin")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("named")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("before")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("100.0%")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.0%")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("100.0% — all of it the handler")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("after")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("44.7%")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("49.7%")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("99.1%")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If you take one thing from this article, take that one: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("a pipeline tested only with synthetic inputs is green while the collector at its head is broken.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-same-program-on-two-platforms-is-two-different-looking-programs") }) {
        org.jetbrains.compose.web.dom.Text("The same program on two platforms is two different-looking programs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Profile the identical source on Linux and on an Apple arm64 machine and you get the same package names in a different order:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("package")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("linuxX64")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("macosArm64")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("kotlin.collections")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("41.1%")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("51.0%")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("c")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("47.5%")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("24.8%")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("outside the binary")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1.4%")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("16.4%")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("macOS links libsystem dynamically, so the allocator frames are outside the image and can only be reported as such; the Linux build carries the same code statically inside it, where it is named. Same work, two accounting treatments. A profiler that folded those frames into the nearest Kotlin one would make the two runs look like two different programs.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("and-finally-the-tool-measuring-itself") }) {
        org.jetbrains.compose.web.dom.Text("And finally, the tool measuring itself")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Adding an MCP server to the razves command took it from ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("3,315,112 to 6,948,816 bytes")
        }
        org.jetbrains.compose.web.dom.Text(" — it more than doubled. Asked about itself, razves named the reason: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("io.modelcontextprotocol.kotlin")
        }
        org.jetbrains.compose.web.dom.Text(" at ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("1,235,919 bytes across 5,858 symbols")
        }
        org.jetbrains.compose.web.dom.Text(", the largest package in the file, with coroutines and Ktor behind it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A tool whose entire argument is \"where did your bytes go\" cannot ship that in the command people run to find out. It is a second binary now. That decision took ten minutes and one measurement, which is the point of having the measurement.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("razves is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/razves") {
            org.jetbrains.compose.web.dom.Text("on GitHub")
        }
        org.jetbrains.compose.web.dom.Text(", published as snapshots, and reads any Kotlin/Native binary you point it at — including the one you are about to be surprised by.")
    }
}
